import { useEffect, useState } from "react";
import { api, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { ShieldAlert, AlertTriangle, Info, CheckCircle2 } from "lucide-react";

const SEV = {
  high: { cls: "border-red-200 bg-red-50", badge: "bg-red-100 text-red-700", Icon: ShieldAlert, label: "High" },
  medium: { cls: "border-amber-200 bg-amber-50", badge: "bg-amber-100 text-amber-800", Icon: AlertTriangle, label: "Caution" },
  info: { cls: "border-blue-200 bg-blue-50", badge: "bg-blue-100 text-blue-700", Icon: Info, label: "Note" },
};

export function ClauseWizard({ defaultState = "", defaultDocType = "prenuptial" }) {
  const [states, setStates] = useState([]);
  const [clauses, setClauses] = useState([]);
  const [state, setState] = useState(defaultState || "");
  const [docType, setDocType] = useState(defaultDocType);
  const [selected, setSelected] = useState([]);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/legal/states").then((r) => setStates(r.data.states)).catch(() => {});
    api.get("/legal/clauses").then((r) => setClauses(r.data.clauses)).catch(() => {});
  }, []);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const run = async () => {
    if (!state) { toast.error("Choose a state first."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/legal/clause-check", { state, doc_type: docType, clauses: selected });
      setResult(data);
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className="border border-[#E2E8F0] rounded-2xl p-5 bg-white" data-testid="clause-wizard">
      <div className="flex items-center gap-2 mb-1"><ShieldAlert className="w-5 h-5 text-[#4A6FA5]" /><h3 className="font-display text-lg font-bold text-[#0B132B]">Clause Safety Check</h3></div>
      <p className="text-sm text-[#5C6B73] mb-4">Pick your state and the clauses you're considering. We'll flag anything that may be unenforceable or work against you, with the rule behind each flag. Advisory only.</p>

      <div className="grid sm:grid-cols-2 gap-3 mb-4">
        <div>
          <label className="text-xs font-semibold text-[#0B132B]">State</label>
          <select data-testid="wizard-state" value={state} onChange={(e) => setState(e.target.value)} className="mt-1 w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm">
            <option value="">Select a state…</option>
            {states.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-[#0B132B]">Agreement type</label>
          <div className="mt-1 flex gap-2">
            {["prenuptial", "postnuptial"].map((d) => (
              <button key={d} data-testid={`wizard-doctype-${d}`} onClick={() => setDocType(d)} className={`flex-1 text-sm rounded-lg py-2 border ${docType === d ? "border-[#0B132B] bg-[#0B132B] text-white" : "border-[#E2E8F0] text-[#0B132B]"}`}>{d === "prenuptial" ? "Prenup" : "Postnup"}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {clauses.map((c) => (
          <button key={c.id} data-testid={`wizard-clause-${c.id}`} onClick={() => toggle(c.id)} title={c.summary}
            className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${selected.includes(c.id) ? "border-[#4A6FA5] bg-[#4A6FA5] text-white" : "border-[#E2E8F0] text-[#5C6B73] hover:border-[#4A6FA5]"}`}>{c.name}</button>
        ))}
      </div>

      <button onClick={run} disabled={busy} data-testid="wizard-run" className="bg-[#0B132B] text-white text-sm px-5 py-2.5 rounded-full font-medium hover:bg-[#1C2541] transition-colors disabled:opacity-60">{busy ? "Checking…" : "Run clause check"}</button>

      {result && (
        <div className="mt-5" data-testid="wizard-results">
          {result.flags.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-xl p-4"><CheckCircle2 className="w-5 h-5" />No red flags for the clauses you selected in {states.find((s) => s.code === result.state)?.name || result.state}. Still have an attorney review before signing.</div>
          ) : (
            <div className="space-y-3">
              {result.flags.map((f) => {
                const sv = SEV[f.severity] || SEV.info; const Ic = sv.Icon;
                return (
                  <div key={f.id} className={`border rounded-xl p-4 ${sv.cls}`} data-testid={`wizard-flag-${f.id}`}>
                    <div className="flex items-start gap-2">
                      <Ic className="w-4 h-4 mt-0.5 shrink-0 text-[#0B132B]" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap"><span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full ${sv.badge}`}>{sv.label}</span><span className="font-semibold text-[#0B132B] text-sm">{f.title}</span></div>
                        <p className="text-sm text-[#2A3441] mt-1">{f.message}</p>
                        {f.recommendation && <p className="text-sm text-[#0B132B] mt-1"><strong>What to do:</strong> {f.recommendation}</p>}
                        <p className="text-xs text-[#5C6B73] mt-1">Source: {f.citation}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <p className="text-xs text-[#5C6B73] mt-3">{result.disclaimer}</p>
        </div>
      )}
    </div>
  );
}
